import { IRoutePolicy } from '@sp-types/index';
import { IRoute, IRoutePrices } from '@sp-types/routeType';
import { sortByOrder } from '../pages/Policy/MaterialDetails/components/MaterialDetailUtils';
import { IBalanceInstruction } from '../sp-types/balanceFlowType';

export const getRouteTotalPrice = (routePrices: IRoutePrices): number =>
  Object.values(routePrices).reduce((total, item) => total + item.price, 0);

export const getRouteTotalCollateral = (routePrices: IRoutePrices): number =>
  Object.values(routePrices).reduce((total, item) => total + item.collateral, 0);

export const makeMap = function <T>(items: T[], idColumn: keyof T & string) {
  return items.reduce(
    (result, item) => ({ ...result, [item[idColumn]?.toString() || '']: item }),
    {} as Record<string, T>,
  );
};

export const makeSumMap = function <T>(items: T[], idColumn: keyof T & string, sumColumn: keyof T & string) {
  return items.reduce(
    (aggregator, item) => ({
      ...aggregator,
      [item[idColumn]?.toString() || '']: item[sumColumn] + (aggregator[item[idColumn]?.toString() || ''] || 0),
    }),
    {} as Record<string, any>,
  );
};

export const applyRoutePoliciesOrder = (materialRoutes: IRoute[], routePolicies: IRoutePolicy[]): IRoute[] => {
  const policiesMap = makeMap(routePolicies, 'routeId');

  return materialRoutes.map(({ order, ...route }) => ({
    ...route,
    order: policiesMap[route.id]?.order || order,
  }));
};

export const makeRoutePolicy = (
  { order, ...route }: IRoute,
  accountId: string,
  materialId: string,
  policy?: IRoutePolicy,
): IRoutePolicy => ({
  routeId: route.id,
  materialId,
  accountId,
  order,
  higherThanIndex: policy?.higherThanIndex || 0,
  higherThanLowest: policy?.higherThanLowest || 0,
  lowerThanNext: policy?.lowerThanNext || 0,
});

const makeRouteQuantityMap = (
  instructions: IBalanceInstruction[],
): Record<string, { quantity: number; tranPrice: number }> =>
  instructions
    .map(({ quantityPerContract, numberOfContracts, routeId, lastPrice }) => ({
      quantity: quantityPerContract * numberOfContracts,
      lastPrice: quantityPerContract * numberOfContracts * lastPrice,
      routeId,
    }))
    .reduce(
      (aggregator, { quantity, routeId, lastPrice }) => ({
        ...aggregator,
        [routeId]: {
          quantity: quantity + (aggregator[routeId]?.quantity || 0),
          tranPrice: lastPrice + (aggregator[routeId]?.tranPrice || 0),
        },
      }),
      {} as Record<string, { quantity: number; tranPrice: number }>,
    );

export const mergeRoutesAndPolicies = (
  routes: IRoute[],
  policies: IRoutePolicy[],
  instructions?: IBalanceInstruction[],
): IRoute[] => {
  const policiesMap = makeMap(policies, 'routeId');

  const quantityPerRoute = (instructions && makeRouteQuantityMap(instructions)) || {};

  const sortedList = routes
    .map(({ id, order, ...route }) => ({
      id,
      routeId: id,
      ...route,
      order: (id && policiesMap[id]?.order) || order,
      instructionsQuantity: quantityPerRoute[id]?.quantity || 0,
      tranPrice: quantityPerRoute[id]?.tranPrice || 0,
    }))
    .sort(sortByOrder);

  return sortedList;
};
